import React, { useState } from 'react';

/**
 * SignedReceipt component — displays a Tier 1 cryptographic receipt.
 *
 * The receipt is a JSON document signed with the issuer's ECDSA private key.
 * It can be verified offline using the included signing certificate, without
 * querying the blockchain.
 */
function SignedReceipt({ receipt }) {
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  if (!receipt) return null;

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(receipt, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${receipt.receiptId?.substring(0, 8) || 'unknown'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(receipt, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const operationLabel = {
    DirectTransfer: 'Instant Transfer',
    CreateAgreement: 'Escrow Created',
    VerifyAgreement: 'Funds Released',
    AcceptAgreement: 'Agreement Accepted',
  }[receipt.operation] || receipt.operation;

  return (
    <div style={{
      marginTop: '15px',
      border: '2px solid #d4a017',
      borderRadius: '12px',
      background: 'white',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(212, 160, 23, 0.2)'
    }}>
      {/* Header banner */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'linear-gradient(135deg, #744210 0%, #d4a017 100%)',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '1rem', color: 'white' }}>
              Signed Transaction Receipt
            </div>
            <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)', marginTop: '2px' }}>
              {operationLabel} &nbsp;·&nbsp;
              {receipt.signed
                ? '✓ Cryptographically signed — verifiable offline'
                : '⚠ Not signed'
              }
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(); }}
            style={{
              padding: '6px 14px', fontSize: '0.82rem',
              background: 'rgba(255,255,255,0.25)', color: 'white',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '6px', cursor: 'pointer', fontWeight: '600'
            }}
          >
            Download
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleCopy(); }}
            style={{
              padding: '6px 14px', fontSize: '0.82rem',
              background: copied ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.15)',
              color: 'white', border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '6px', cursor: 'pointer', fontWeight: '600'
            }}
          >
            {copied ? 'Copied' : 'Copy JSON'}
          </button>
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ padding: '16px 18px', background: '#fffdf0' }}>
          {/* Key fields */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
            marginTop: '14px',
            marginBottom: '14px'
          }}>
            {[
              ['Receipt ID', receipt.receiptId?.substring(0, 16) + '...'],
              ['Transaction ID', receipt.txId?.substring(0, 16) + '...'],
              ['Operation', operationLabel],
              ['Amount', `${receipt.amount?.toLocaleString()} Centro`],
              ['Status', receipt.status],
              ['Block', receipt.blockNumber !== null ? `#${receipt.blockNumber}` : 'N/A'],
              ['Channel', receipt.channel],
              ['Issuer', receipt.issuer],
              ['Timestamp', receipt.timestamp ? new Date(receipt.timestamp).toLocaleString() : 'N/A'],
            ].map(([label, value]) => (
              <div key={label} style={{ fontSize: '0.82rem' }}>
                <span style={{ color: '#4a5568', fontWeight: '600' }}>{label}: </span>
                <span style={{ color: '#2d3748', fontFamily: 'monospace' }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Parties */}
          {receipt.parties && (
            <div style={{ marginBottom: '14px', fontSize: '0.82rem' }}>
              <div style={{ color: '#4a5568', fontWeight: '600', marginBottom: '6px' }}>Parties</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(receipt.parties).filter(([, v]) => v).map(([role, id]) => (
                  <span key={role} style={{
                    padding: '3px 8px',
                    background: '#bee3f8',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem'
                  }}>
                    {role}: {id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Signature */}
          {receipt.signed && receipt.signature && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ color: '#4a5568', fontWeight: '600', fontSize: '0.82rem', marginBottom: '4px' }}>
                ECDSA Signature
              </div>
              <div style={{
                fontFamily: 'monospace',
                fontSize: '0.72rem',
                background: '#fff',
                border: '1px solid #c3dafe',
                borderRadius: '6px',
                padding: '8px',
                wordBreak: 'break-all',
                color: '#2d3748'
              }}>
                {receipt.signature}
              </div>
            </div>
          )}

          {/* Cert fingerprint */}
          {receipt.certFingerprint && (
            <div style={{ fontSize: '0.8rem' }}>
              <span style={{ color: '#4a5568', fontWeight: '600' }}>Cert Fingerprint (SHA-256): </span>
              <span style={{ fontFamily: 'monospace', color: '#2d3748', fontSize: '0.72rem' }}>
                {receipt.certFingerprint}
              </span>
            </div>
          )}

          {receipt.signingError && (
            <div style={{ fontSize: '0.8rem', color: '#744210', marginTop: '8px' }}>
              Signing note: {receipt.signingError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SignedReceipt;
